import { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_EQUIPE_E_PARCEIROS } from "@/lib/ajuda/dialogos/equipe-e-parceiros";
import { env } from "@/lib/env";
import { ResultStage, DialogField, formatExpiry } from "@/components/shared/InviteResultStage";

type PartnerConnectTarget = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
};

type Props = {
  partner: PartnerConnectTarget | null;
  onClose: () => void;
};

type Stage = { kind: "form" } | { kind: "result"; token: string; email: string | null; expiresAt: Date | null };

/**
 * Convida um dentista de uma ficha existente (Partner) pra plataforma — a "ponte": o aceite
 * ATUALIZA a ficha (preservando histórico e preços) em vez de criar registro novo.
 *
 * Mesmos canais do convite avulso (ConvidarParceiroDialog): com email — pré-preenchido da própria
 * ficha — a plataforma envia o convite e, se a pessoa já tiver conta, ele também aparece dentro do
 * app; sem email, sai só o link. A resposta é idêntica exista ou não conta com o endereço.
 */
export function PartnerConnectDialog({ partner, onClose }: Props) {
  const open = !!partner;
  const utils = trpc.useUtils();
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    setStage({ kind: "form" });
    setEmail(partner?.email ?? "");
    setCopied(false);
    setShowQr(false);
  }, [partner?.id, partner?.email]);

  const createInvite = trpc.partner.connectInvite.create.useMutation({
    onSuccess: (invite) => {
      setStage({
        kind: "result",
        token: invite.token,
        email: invite.email ?? null,
        expiresAt: invite.expiresAt ? new Date(invite.expiresAt) : null,
      });
    },
    onError: (err) => toast.error(err.message || "Erro ao gerar convite."),
  });

  const enviar = () => {
    if (!partner) return;
    createInvite.mutate({ partnerId: partner.id, email: email.trim() || undefined });
  };

  const handleClose = () => {
    onClose();
    utils.partner.list.invalidate();
  };

  const inviteUrl = stage.kind === "result" ? `${env.landingUrl}/conectar-dentista/${stage.token}` : "";
  const phone = partner?.phone ?? null;

  const toWaNumber = (v: string) => {
    const d = v.replace(/\D/g, "");
    return d.startsWith("55") ? d : `55${d}`;
  };
  const whatsappText = `Olá! Conecte sua clínica ao DentalBook pra fazer pedidos de prótese direto pela plataforma: ${inviteUrl}`;
  const whatsappHref = phone
    ? `https://wa.me/${toWaNumber(phone)}?text=${encodeURIComponent(whatsappText)}`
    : `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  const emailSubject = "Conectar sua clínica no DentalBook";
  const emailBody = `Olá!%0D%0A%0D%0APra fazer pedidos de prótese direto pela plataforma, acesse:%0D%0A${encodeURIComponent(inviteUrl)}%0D%0A%0D%0AObrigado!`;
  const mailtoHref = partner?.email
    ? `mailto:${partner.email}?subject=${encodeURIComponent(emailSubject)}&body=${emailBody}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle className="dark:text-foreground" ajuda={AJUDA_EQUIPE_E_PARCEIROS.convidarDentistaParaPlataforma}>
            Convidar dentista pra plataforma
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {stage.kind === "form"
              ? `Ao aceitar, ${partner?.name} conecta a conta real da clínica e este cadastro é atualizado — o histórico e os preços ficam.`
              : stage.email
                ? "O email já foi enviado. O link abaixo é o mesmo convite — use como reforço no WhatsApp."
                : `Envie esse link pra ${partner?.name} — ao aceitar, a conexão atualiza este cadastro.`}
          </DialogDescription>
        </DialogHeader>

        {stage.kind === "form" ? (
          <div className="space-y-3">
            <DialogField icon={Mail} label="Email do dentista (opcional)">
              <input
                type="email"
                name="bd-partner-bridge-email"
                autoComplete="off"
                data-1p-ignore
                data-lpignore
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="dentista@exemplo.com"
                className="w-full bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 sm:text-sm dark:text-foreground dark:placeholder:text-muted-foreground"
              />
            </DialogField>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-11 flex-1 items-center justify-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={createInvite.isPending}
                onClick={enviar}
                className="inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:flex-none"
              >
                {createInvite.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {email.trim() ? "Enviar convite" : "Gerar link"}
              </button>
            </div>
          </div>
        ) : (
          <ResultStage
            inviteUrl={inviteUrl}
            phone={phone}
            email={stage.email ?? partner?.email ?? null}
            whatsappHref={whatsappHref}
            mailtoHref={mailtoHref}
            copied={copied}
            onCopy={copy}
            showQr={showQr}
            onToggleQr={() => setShowQr((v) => !v)}
            expiryLabel={stage.expiresAt ? formatExpiry(stage.expiresAt) : null}
            onNewLink={() => setStage({ kind: "form" })}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
